import type { WeightHistoryDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { type WeightHistoryDocTypeV0, weightHistorySchemaLiteral0 } from './migrations/weighthistory';
import {
  defineRxdbCollectionModel,
  defineRxdbMigration,
  defineRxdbMigrations,
  toRxdbMigrationStrategies,
} from './migration';

// ============================================================================
// RxDB collection schema for weightHistory.
//
// Document shape mirrors the D1 `weight_history` row. Uses `updatedAtUtc` as
// the checkpoint field (not `updatedAt`).
// ============================================================================

export const WEIGHT_HISTORY_COLLECTION = 'weightHistory' as const;


export const weightHistorySchemaLiteral1 = {
  title: 'weight history schema',
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    weightKg: { type: 'number' },
    measuredAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    localDate: { type: 'string', maxLength: 10 },
    source: { type: ['string', 'null'] },
    notes: { type: ['string', 'null'] },
    createdAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    updatedAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'weightKg', 'measuredAtUtc', 'localDate', 'source', 'notes', 'createdAtUtc', 'updatedAtUtc', 'deletedAt'],
  indexes: [['userId', 'updatedAtUtc', 'measuredAtUtc']]
} as const;
export type WeightHistoryDocTypeV1 = ExtractDocumentTypeFromTypedRxJsonSchema<typeof weightHistorySchemaLiteral1>;

// v2: body composition. `bodyFatPct` (percent) drives fat-free-mass in the
// energy-availability calc; `measurements` is an opaque nested object holding
// metric circumferences (cm) and skinfolds (mm) — see BodyMeasurements.
export const weightHistorySchemaLiteral2 = {
  title: 'weight history schema',
  version: 2,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    weightKg: { type: 'number' },
    measuredAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    localDate: { type: 'string', maxLength: 10 },
    source: { type: ['string', 'null'] },
    notes: { type: ['string', 'null'] },
    bodyFatPct: { type: ['number', 'null'] },
    measurements: { type: ['object', 'null'] },
    createdAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    updatedAtUtc: { type: 'string', format: 'date-time', maxLength: 33 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'weightKg', 'measuredAtUtc', 'localDate', 'source', 'notes', 'bodyFatPct', 'measurements', 'createdAtUtc', 'updatedAtUtc', 'deletedAt'],
  indexes: [['userId', 'updatedAtUtc', 'measuredAtUtc']]
} as const;
export type WeightHistoryDocTypeV2 = ExtractDocumentTypeFromTypedRxJsonSchema<typeof weightHistorySchemaLiteral2>;

export const weightHistorySchemaLiteral = weightHistorySchemaLiteral2;
export const WEIGHT_HISTORY_SCHEMA_VERSION = weightHistorySchemaLiteral.version;

export interface WeightHistoryCheckpoint {
  updatedAtUtc: string;
  id: string;
}

// `measurements` is a generic object in the RxDB schema; override it with the
// precise validator type so the doc type matches WeightHistoryDocType (mirrors
// how diaryDays overrides its opaque `body` field).
type _WeightHistorySchemaDocType = Omit<
  ExtractDocumentTypeFromTypedRxJsonSchema<typeof weightHistorySchemaLiteral>,
  'measurements'
> & { measurements: WeightHistoryDocType['measurements'] };
type _WeightHistoryDocTypeInSync = Assert<AssertEqual<_WeightHistorySchemaDocType, WeightHistoryDocType>>;

export const weightHistorySchema: RxJsonSchema<WeightHistoryDocType> = weightHistorySchemaLiteral;

export const weightHistoryMigrationSteps = defineRxdbMigrations(
  defineRxdbMigration<WeightHistoryDocTypeV0, WeightHistoryDocTypeV1, 1>(
    1,
    (old) => ({
      ...old,
      source: old.source ?? null,
      notes: old.notes ?? null,
      deletedAt: old.deletedAt ?? null,
    }),
  ),
  defineRxdbMigration<WeightHistoryDocTypeV1, WeightHistoryDocTypeV2, 2>(
    2,
    (old) => ({
      ...old,
      bodyFatPct: null,
      measurements: null,
    }),
  ),
);

export const weightHistoryMigrations = toRxdbMigrationStrategies(weightHistoryMigrationSteps);

export const weightHistoryModel = defineRxdbCollectionModel<typeof WEIGHT_HISTORY_COLLECTION, WeightHistoryDocType>({
  collection: WEIGHT_HISTORY_COLLECTION,
  schema: weightHistorySchema,
  migrationStrategies: weightHistoryMigrations,
});
